import React, { useState, useRef } from "react";
import { useTheme, List, Button, Pagination, Box } from "@mui/material";
import WidgetAlert, { WidgetErrorType } from "components/widgetParts/WidgetAlert";
import WidgetLayout from "components/widgetParts/WidgetLayout";
import { useTranslation } from 'react-i18next';
import { Label, Post } from "./PostListUtils";
import PostListItem from "./PostListItem";

interface PostListContentProps {
  posts: Post[] | undefined;
  labels: Label | undefined;
  cursor: string | null;
  pageSize: number;
  isFetchingMore?: boolean;
  onFetchMore?: () => void;
  onScrollToRef?: () => void;
}

export default function PostListContent({ posts, labels, cursor, pageSize, isFetchingMore, onFetchMore, onScrollToRef }: PostListContentProps): React.JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();

  const [pageNumber, setPageNumber] = useState<number>(1);
  const pageCount = Math.ceil((posts?.length ?? 0) / pageSize);
  const paginatedRecords = (posts ?? []).slice((pageNumber - 1) * pageSize, pageNumber * pageSize);

  // If Dialog handles scrolling, we're able to hide inner scrollbar. ie: Drilldown Dialog
  const isEmbeddedInDialog = !!onScrollToRef;

  // Scroll to top on page change
  const listRef = useRef<HTMLUListElement>(null);
  const scrollToTop = () => {
    if (onScrollToRef) {
      // If a scroll function is provided, use it (for scrolling within a dialog)
      onScrollToRef();
    } else {
      // Default to scrolling window to top when not in drilldown dialog
      listRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPageNumber(value);
    scrollToTop();
  };

  // Fetch more records if at the end of the list
  const isAtEnd = pageNumber === pageCount && cursor !== null;
  const handleFetchMore = () => {
    if (onFetchMore) {
      onFetchMore();
      scrollToTop();
    }
  };

  // Advance to the next page after fetch, and scroll back up
  const [previousPostsLength, setPreviousPostsLength] = useState(posts?.length ?? 0);
  if ((posts?.length ?? 0) !== previousPostsLength) {
    if ((posts?.length ?? 0) > previousPostsLength) {
      setPageNumber((prevPageNumber) => prevPageNumber + 1);
      scrollToTop();
    }
    setPreviousPostsLength(posts?.length ?? 0);
  }

  // Render the list of posts, with pagination and "Load More" button if applicable
  const postListItems = paginatedRecords.map((post:Post, index:number) => (
    <PostListItem key={post._id} index={index} post={post} labels={labels} />
  ));

  return (
    <WidgetLayout
      footer={(posts?.length ?? 0) > pageSize && (
        <Box sx={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center", 
          p: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper }} >
          <Pagination
            count={pageCount}
            onChange={handlePageChange}
            page={pageNumber}
            shape="rounded" />

          {onFetchMore && isAtEnd && 
            <Box>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                onClick={handleFetchMore}
                disabled={isFetchingMore}
                sx={{ fontSize: "0.8rem", px: 1, py: 0.5 }}
              >
                {i18n.t("widget-types.post-list.load-more")}
              </Button>
            </Box>
          }
        </Box>
      )}
    >
      {(posts?.length ?? 0) > 0 && 
        <List ref={listRef} sx={{ height: isEmbeddedInDialog ? "auto" : "100%", width: "100%", px: 0, py: 1, overflowY: isEmbeddedInDialog ? "visible" : "scroll" }}>
          <Box className="post-list-image-content"> 
            {postListItems}
          </Box>
        </List>
      }
      {!(posts?.length ?? 0) &&
        <WidgetAlert type={WidgetErrorType.NO_DATA} />
      }
    </WidgetLayout>
  );
}
